import { useCallback, useEffect, useRef, useState } from "react";

export type GeoPoint = {
  lat: number;
  lon: number;
  accuracyM: number;        // 1-sigma radius in meters
  altitudeM: number | null;
  altitudeAccuracyM: number | null;
  headingDeg: number | null;
  speedMps: number | null;
  at: number;               // unix ms
};

export type GeoStatus =
  | "idle"
  | "prompting"             // OS-level permission dialog showing
  | "denied"                // user denied or system-blocked
  | "unavailable"           // device cannot provide a fix
  | "timeout"               // could not get a fix in time
  | "locating"              // requesting first fix
  | "tracking";             // we have a fix and are watching

export type LocationState = {
  status: GeoStatus;
  current: GeoPoint | null;
  history: GeoPoint[];      // recent points (capped)
  error: string | null;
};

const MAX_HISTORY = 200;
const PRIMARY_TIMEOUT_MS = 15_000;
const STALE_AFTER_MS = 90_000;
const MAX_PRIMARY_RETRIES = 2;

function toPoint(p: GeolocationPosition): GeoPoint {
  return {
    lat: p.coords.latitude,
    lon: p.coords.longitude,
    accuracyM: p.coords.accuracy,
    altitudeM: p.coords.altitude,
    altitudeAccuracyM: p.coords.altitudeAccuracy,
    headingDeg: p.coords.heading,
    speedMps: p.coords.speed,
    at: p.timestamp || Date.now(),
  };
}

function errMessage(e: GeolocationPositionError): { status: GeoStatus; message: string } {
  if (e.code === 1) return { status: "denied", message: "Location permission denied" };
  if (e.code === 2) return { status: "unavailable", message: "Device cannot determine your location" };
  if (e.code === 3) return { status: "timeout", message: "Location request timed out" };
  return { status: "unavailable", message: e.message || "Unknown geolocation error" };
}

/**
 * Hook that hard-requires high-accuracy device geolocation.
 *
 * - Asks for permission immediately
 * - Acquires a first fix with high accuracy
 * - Watches continuously, keeping a recent history
 * - Calls `onUpdate` for each new point so callers can ship updates upstream
 */
export function useLocation(onUpdate?: (p: GeoPoint) => void | Promise<void>) {
  const [state, setState] = useState<LocationState>({
    status: "idle",
    current: null,
    history: [],
    error: null,
  });
  const watchId = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const primaryRetries = useRef(0);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const stop = useCallback(() => {
    if (retryTimer.current !== null) {
      window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    primaryRetries.current = 0;
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
  }, []);

  const requestFix = useCallback((attempt: number) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const point = toPoint(pos);
        setState((s) => ({
          status: "tracking",
          current: point,
          history: [...s.history.slice(-MAX_HISTORY + 1), point],
          error: null,
        }));
        void onUpdateRef.current?.(point);
      },
      (err) => {
        const e = errMessage(err);
        if (e.status !== "denied" && attempt < MAX_PRIMARY_RETRIES) {
          retryTimer.current = window.setTimeout(() => {
            requestFix(attempt + 1);
          }, 1200);
          setState((s) => ({ ...s, status: "locating", error: null }));
          return;
        }
        setState({ status: e.status, current: null, history: [], error: e.message });
      },
      { enableHighAccuracy: true, timeout: PRIMARY_TIMEOUT_MS, maximumAge: 0 }
    );
  }, []);

  const start = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setState({
        status: "unavailable",
        current: null,
        history: [],
        error: "This browser does not support geolocation",
      });
      return;
    }

    setState((s) => ({ ...s, status: s.current ? s.status : "prompting", error: null }));

    stop();
    primaryRetries.current = 0;
    requestFix(primaryRetries.current);
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const point = toPoint(pos);
        setState((s) => ({
          status: "tracking",
          current: point,
          history: [...s.history.slice(-MAX_HISTORY + 1), point],
          error: null,
        }));
        void onUpdateRef.current?.(point);
      },
      (err) => {
        // watch errors are non-fatal if we already have a fix
        setState((s) => {
          if (s.current) return { ...s, error: errMessage(err).message };
          const e = errMessage(err);
          return { status: e.status, current: null, history: [], error: e.message };
        });
      },
      { enableHighAccuracy: true, timeout: 30_000, maximumAge: 5_000 }
    );
  }, [requestFix, stop]);

  useEffect(() => () => stop(), [stop]);

  // Mark fix as stale (not "lost") if updates stop coming for a while
  useEffect(() => {
    if (!state.current) return;
    const handle = window.setInterval(() => {
      setState((s) => {
        if (!s.current) return s;
        if (Date.now() - s.current.at > STALE_AFTER_MS && !s.error) {
          return { ...s, error: "Location may be stale — last update >90s ago" };
        }
        return s;
      });
    }, 5_000);
    return () => window.clearInterval(handle);
  }, [state.current]);

  return { state, start, stop };
}

export function accuracyLabel(accuracyM: number): { tier: "high" | "medium" | "low"; label: string } {
  if (accuracyM <= 25) return { tier: "high", label: `±${Math.round(accuracyM)} m (high accuracy)` };
  if (accuracyM <= 200) return { tier: "medium", label: `±${Math.round(accuracyM)} m (medium accuracy)` };
  return { tier: "low", label: `±${Math.round(accuracyM)} m (low accuracy — likely WiFi/cell)` };
}
